import { useState } from "react";
import { W, H, toXY, SH_PATH, HH_PATH } from "../../data/shKarte";
import { BETRIEB, ORTE } from "../../data/orte";
import { REDUCED_MOTION } from "../../reducedMotion";

/**
 * Gezeichnete Karte von Schleswig-Holstein mit einer Nadel je Referenz.
 * Klick/Tippen auf eine Nadel zeigt das Projekt in der Karte daneben; „Zum Projekt“ scrollt zur ausführlichen Karte.
 * Props: referenzen = Liste aus data/referenzen.js, bereits mit lat/lon ergänzt (standorte() in orte.js). Reihenfolge = Nummer der Nadel.
 * Einträge ohne lat/lon bekommen eine gestrichelte Nadel (.is-ohne-ort) an einem beispielhaften Platz (BEISPIEL_PLAETZE)
 * und einen Hinweis unter der Karte; sie dürfen dort nicht wie ein echter Ort wirken.
 */
// Ortsname rechts vom Punkt oder links darunter, damit er nicht unter den Nadeln liegt
const ORIENTIERUNG = [["Flensburg", "right"], ["Kiel", "below"], ["Lübeck", "right"]];
// Beispielhafte Plätze im Land für Projekte ohne Ortsangabe (Breite, Länge), über das Land verteilt
// Bewusst nicht in der Nähe nicht genannter echter Orte (Deponie) und nicht am Schriftzug „Betriebssitz“
const BEISPIEL_PLAETZE = [[54.52, 9.56], [54.2, 9.4], [54.29, 10.88], [54.3, 9.66], [54.07, 9.98], [53.94, 10.31]];
const pct = ([x, y]) => ({ left: `${(x / W) * 100}%`, top: `${(y / H) * 100}%` });
// Vorschaubild: Nachher-Bild des Vergleichs, sonst das erste Foto – jeweils mit Pixelmaßen
const vorschau = (r) =>
  r.vergleich ? { src: r.vergleich.nachher, breite: r.vergleich.breite, hoehe: r.vergleich.hoehe } : r.bilder?.[0];
const anriss = (r) => {
  const t = Array.isArray(r.text) ? r.text[0] : r.text;
  return t.length > 170 ? `${t.slice(0, t.lastIndexOf(" ", 165))} …` : t;
};

export default function ReferenzKarte({ referenzen }) {
  const mitOrt = (x) => x.lat != null && x.lon != null;
  const ohneOrt = referenzen.filter((x) => !mitOrt(x));
  const nadeln = referenzen;
  const [aktiv, setAktiv] = useState(referenzen[0]?.id);
  const r = referenzen.find((x) => x.id === aktiv) || referenzen[0];
  const position = (x) => (mitOrt(x) ? toXY(x.lat, x.lon) : toXY(...BEISPIEL_PLAETZE[ohneOrt.indexOf(x) % BEISPIEL_PLAETZE.length]));
  const nr = referenzen.indexOf(r) + 1;
  const [bx, by] = toXY(BETRIEB.lat, BETRIEB.lon);
  const [hx, hy] = toXY(53.57, 10.0);

  const zumProjekt = () =>
    document.getElementById(`ref-${r.id}`)?.scrollIntoView({ behavior: REDUCED_MOTION ? "auto" : "smooth", block: "start" });

  return (
    <div className="ff-map">
      <div className="ff-map__stage" style={{ aspectRatio: `${W} / ${H}` }}>
        <svg className="ff-map__svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Karte von Schleswig-Holstein mit den Referenzprojekten">
          <defs>
            <pattern id="ff-hatch" width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(35)">
              <line x1="0" y1="0" x2="0" y2="5" className="ff-map__hatch" />
            </pattern>
            <filter id="ff-sketch" x="-5%" y="-5%" width="110%" height="110%">
              <feTurbulence type="fractalNoise" baseFrequency="0.035" numOctaves="2" seed="4" />
              <feDisplacementMap in="SourceGraphic" scale="2.6" />
            </filter>
          </defs>

          <text x="20" y="215" className="ff-map__sea">Nordsee</text>
          <text x="300" y="70" className="ff-map__sea">Ostsee</text>

          <path d={SH_PATH} className="ff-map__land" />
          <path d={SH_PATH} fill="url(#ff-hatch)" />
          <g filter="url(#ff-sketch)">
            <path d={SH_PATH} className="ff-map__coast" />
            <path d={SH_PATH} className="ff-map__coast ff-map__coast--echo" transform="translate(1.2 0.8)" />
            <path d={HH_PATH} className="ff-map__hh" />
          </g>

          {ORIENTIERUNG.map(([o, anchor]) => {
            const [x, y] = toXY(...ORTE[o]);
            return (
              <g key={o} className="ff-map__town">
                <circle cx={x} cy={y} r="2" />
                {anchor === "below" ? (
                  <text x={x - 4} y={y + 12} textAnchor="end">{o}</text>
                ) : (
                  <text x={x + 5} y={y + 3.5}>{o}</text>
                )}
              </g>
            );
          })}
          <text x={hx - 26} y={hy + 18} className="ff-map__town ff-map__town--hh">Hamburg</text>

          {/* Haus-Symbol etwas links vom Ort, damit eine Nadel in Probsteierhagen es nicht verdeckt */}
          <g className="ff-map__base" transform={`translate(${bx - 14} ${by - 4})`}>
            <path d="M0 -9 L8 -2 L5.5 -2 L5.5 6 L-5.5 6 L-5.5 -2 L-8 -2 Z" />
            <text x="0" y="-17" textAnchor="middle">Betriebssitz</text>
          </g>
        </svg>

        {nadeln.map((x) => (
          <button
            key={x.id}
            type="button"
            className={`ff-map__pin ${x.id === r.id ? "is-active" : ""} ${mitOrt(x) ? "" : "is-ohne-ort"}`}
            style={pct(position(x))}
            onClick={() => setAktiv(x.id)}
            aria-pressed={x.id === r.id}
            aria-label={`Projekt ${referenzen.indexOf(x) + 1}: ${x.titel}`}
          >
            <span>{referenzen.indexOf(x) + 1}</span>
          </button>
        ))}
      </div>

      {r && (
        <div className="ff-map__card" aria-live="polite">
          {vorschau(r) && (
            <img
              src={vorschau(r).src}
              width={vorschau(r).breite}
              height={vorschau(r).hoehe}
              alt=""
              className="ff-map__thumb"
              loading="lazy"
              decoding="async"
            />
          )}
          <div className="ff-map__card-body">
            <span className="ff-eyebrow">
              {nr} · {r.leistung}
              {r.ort ? ` · ${r.ort}` : ""}
              {r.jahr ? ` · ${r.jahr}` : ""}
            </span>
            {/* Kein Überschriften-Element: Der Titel steht als Überschrift im Projektartikel darunter */}
            <p className="ff-article-h3">{r.titel}</p>
            <p className="ff-map__teaser">{anriss(r)}</p>
            <button type="button" className="btn-ff-outline" onClick={zumProjekt}>
              Zum Projekt
            </button>
          </div>
        </div>
      )}

      {ohneOrt.length > 0 && (
        <p className="ff-map__note">
          Gestrichelte Nadeln sind Projekte ohne Ortsangabe: Sie liegen in Schleswig-Holstein, ihre Position auf der Karte ist nur beispielhaft.
        </p>
      )}
    </div>
  );
}
